/**
 * Warioon weight selector.
 * Reads the selected weight band (chews per day) and the selected variant price
 * (updated through Horizon's `variant:update` event) and renders dose, days per jar
 * and the lowest price per day. Presentation only.
 */
class WrWeightSelector extends HTMLElement {
  connectedCallback() {
    if (this.wired) return; // moving the element (see placeWeightSelector) reconnects it
    this.wired = true;
    this.chewsPerJar = Number(this.dataset.chewsPerJar) || 60;
    this.price = Number(this.dataset.price) || 0; // minor units (cents)
    this.jars = Number(this.dataset.jars) || 1;
    this.currency = this.dataset.currency || 'COP';
    this.locale = this.dataset.locale || 'es';
    const tpl = this.querySelector('template[ref="labels"]');
    const parts = (tpl ? tpl.content.textContent : '').trim().split('|');
    this.labels = {
      doseOne: parts[0] || '{n} masticable al día',
      doseRange: parts[1] || '{min} a {max} masticables al día',
      daysOne: parts[2] || 'Un frasco dura {n} días',
      daysRange: parts[3] || 'Un frasco dura {min} a {max} días',
      perDay: parts[4] || 'Desde {price} al día',
    };
    this.formatter = new Intl.NumberFormat(this.locale.startsWith('es') ? 'es-CO' : this.locale, {
      style: 'currency',
      currency: this.currency,
      maximumFractionDigits: 0,
    });

    this.addEventListener('change', () => this.render());

    const section = this.closest('.shopify-section') || document;
    this.onVariantUpdate = (event) => {
      const resource = event.detail && event.detail.resource;
      if (!resource || typeof resource.price !== 'number') return;
      this.price = resource.price;
      const first = String(resource.title || '').trim().split(/\s+/)[0];
      const jars = parseInt(first, 10);
      this.jars = Number.isFinite(jars) && jars > 0 ? jars : 1;
      this.render();
    };
    section.addEventListener('variant:update', this.onVariantUpdate);
    this.render();
  }

  // Stays wired while moved between columns: the section (and its variant:update events) is the same

  get selectedBand() {
    return this.querySelector('input[type="radio"]:checked') || this.querySelector('input[type="radio"]');
  }

  fill(template, values) {
    return template.replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));
  }

  render() {
    const band = this.selectedBand;
    if (!band) return;
    const min = Number(band.dataset.min) || 1;
    const max = Number(band.dataset.max) || min;
    const daysMax = Math.round(this.chewsPerJar / min);
    const daysMin = Math.round(this.chewsPerJar / max);
    const pricePerChewMinor = this.price / (this.jars * this.chewsPerJar);
    const perDayMinor = pricePerChewMinor * min;
    // es-CO puts a space after the symbol; the store's money format has none ($2.500)
    const perDay = this.formatter.format(perDayMinor / 100).replace(/^(\D+?)\s+/, '$1');

    const dose = min === max ? this.fill(this.labels.doseOne, { n: min }) : this.fill(this.labels.doseRange, { min, max });
    const days =
      daysMin === daysMax
        ? this.fill(this.labels.daysOne, { n: daysMax })
        : this.fill(this.labels.daysRange, { min: daysMin, max: daysMax });

    const doseEl = this.querySelector('[ref="dose"]');
    const daysEl = this.querySelector('[ref="days"]');
    const perDayEl = this.querySelector('[ref="perDay"]');
    if (doseEl) doseEl.textContent = dose;
    if (daysEl) daysEl.textContent = days;
    if (perDayEl) perDayEl.textContent = this.price > 0 ? this.fill(this.labels.perDay, { price: perDay }) : '';
  }
}

if (!customElements.get('wr-weight-selector')) {
  customElements.define('wr-weight-selector', WrWeightSelector);
}

/**
 * Desktop: move "¿Cuánto pesa tu perro?" under the product photo to balance the two columns and
 * bring "Comprar ahora" into the first screen. Phones keep it in the buy column. If this script
 * fails the selector simply stays where Liquid rendered it.
 */
function placeWeightSelector() {
  const desktop = window.matchMedia('(min-width: 990px)');
  document.querySelectorAll('.product-information wr-weight-selector').forEach((el) => {
    const media = el.closest('.product-information')?.querySelector('.product-information__media');
    if (!media) return;
    if (!el.wrHome) {
      el.wrHome = document.createComment('wr-weight-selector');
      el.before(el.wrHome);
    }
    let aside = media.querySelector(':scope > .wr-weight-aside');
    if (desktop.matches) {
      if (!aside) {
        aside = document.createElement('div');
        aside.className = 'wr-weight-aside';
        media.append(aside);
      }
      if (el.parentElement !== aside) aside.append(el);
    } else if (el.parentElement === aside) {
      el.wrHome.after(el);
    }
  });
}

placeWeightSelector();
window.matchMedia('(min-width: 990px)').addEventListener('change', placeWeightSelector);
